import { useRef, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import ReactQuill from 'react-quill-new';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { emailReminder } from '../store/Slices/WorkOrderSlice';
import '../styles/Input.css';
import { shouldRenderAutoRequiredMark } from '../utils/requiredLabel';
import { hasPermission, modules } from '../utils/helper';
import { errorNotify, successNotify } from '../utils/toast';

const Input = ({ label, placeholder, type, name, value, onChange, onBlur,
  error, touched, disabled = false, className = '', showExtraText = false, extraText, quill = false, setFieldValue, showIcon = null, onIconClick = null, required = true, minValue, maxValue, min, max, step, tabIndex, readOnly = false, onClick }) => {

  const { id } = useParams();
  const dispatch = useDispatch();

  const { emailReminderLoading } = useSelector((state) => state.workOrder);

  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);
  const shouldShowAutoRequired = shouldRenderAutoRequiredMark(label, required);

  const canFinanceApproval = hasPermission("work-orders", "finance-approval");
  const canOperationalApproval = hasPermission("work-orders", "operations-approval");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const inputType = type === 'password' && showPassword ? 'text' : type;

  const normalizeQuillValue = (content = '') => {
    const rawValue = typeof content === 'string' ? content : '';
    const plainText = rawValue
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\u200B/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    return plainText ? rawValue : '';
  };

  const handleWrapperMouseDown = (e) => {
    if (e.target.tagName !== 'INPUT' && inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  };

  const handleInputWheel = (event) => {
    if (inputType !== 'number') return;

    if (event.currentTarget === document.activeElement) {
      event.currentTarget.blur();
    }
  };

  const emailReminderHandler = async (name) => {
    try {

      if (name === "finance_status" && !canFinanceApproval) {
        throw new Error("You do not have permission for finance approval.");
      }

      // Check for operational approval condition
      if (name === "operations_status" && !canOperationalApproval) {
        throw new Error("You do not have permission for operational approval.");
      }

      let data = {
        work_order_id: parseInt(id),
        reminder_type: name === "finance_status" ? "finance-approval" : "operations-approval",
      }
      await dispatch(emailReminder(data)).unwrap();
      successNotify("Email reminder sent successfully");
    }
    catch (err) {
      errorNotify(err);
    }
  }

  return (
    <div className={`input_wrapper ${className}`} data-field-name={name}>
      <div
        className={`input_container ${disabled ? 'disabled' : ''}`}
        style={{ position: 'relative' }}
        onMouseDown={handleWrapperMouseDown}
      >
        <label style={{ display: "flex", gap: "5px" }}>
          {label}
          {shouldShowAutoRequired && (
            <span style={{ color: "red", fontSize: "13px" }}>*</span>
          )}
        </label>
        {
          quill ? (
            <div style={{ marginTop: '5px' }}>
              <ReactQuill
                placeholder={placeholder}
                modules={modules}
                theme="snow"
                value={value || ''}
                readOnly={Boolean(disabled || readOnly)}
                tabIndex={tabIndex}
                onChange={content => {
                  if (disabled || readOnly) return;
                  const normalizedContent = normalizeQuillValue(content);
                  if (typeof setFieldValue === 'function') {
                    setFieldValue(name, normalizedContent);
                  } else if (typeof onChange === 'function') {
                    onChange(normalizedContent);
                  }
                }}
                onBlur={() => {
                  if (typeof onBlur === 'function') {
                    onBlur({ target: { name } });
                  }
                }}
                name={name}
                className='react_quill_input quill-small-font' // add custom class
              />
            </div>
          ) :
            <input
              ref={inputRef}
              type={inputType}
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              onWheel={handleInputWheel}
              onClick={onClick}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              tabIndex={tabIndex}
              min={min ?? minValue}
              max={max ?? maxValue}
              step={step}
              className={`${disabled ? 'disabled-component' : ''}`}
              style={{
                cursor: disabled ? 'not-allowed' : readOnly ? 'pointer' : 'text',
                textTransform: name === "finance_status" || name === "operation_status" ? 'capitalize' : 'none'
              }}
            />
        }

        {
          showExtraText && <span style={{
            fontSize: '13px',
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            paddingLeft: '5px',
            color: '#888EA8'
          }}>{extraText}</span>
        }

        {
          showIcon && (
            <span
              onClick={
                onIconClick
                  ? onIconClick
                  : (name === "finance_status" || name === "operation_status")
                    ? (emailReminderLoading ? null : value === "rejected" ? null : value === "approved" ? null : () => emailReminderHandler(name))
                    : undefined
              }
              style={{
                position: 'absolute',
                right: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: onIconClick
                  ? 'pointer'
                  : (name === "finance_status" || name === "operation_status") ? (emailReminderLoading ? 'not-allowed' : value === "rejected" ? 'not-allowed' : value === "approved" ? 'not-allowed' : 'pointer') : 'default',
                color: '#666'
              }}
            >
              {showIcon}
            </span>
          )
        }

        {type === 'password' && (
          <span
            onClick={togglePasswordVisibility}
            style={{
              position: 'absolute',
              right: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        )}
      </div>
      {(() => {
        const isRequiredError = typeof error === 'string' && error.toLowerCase().includes('required');
        const hasValue = value !== null && value !== undefined && String(value).trim().length > 0;
        const shouldShowError = touched && error && (!hasValue || !isRequiredError);

        return shouldShowError ? (
          <div className="error-message" style={{ color: '#ff0000de', fontSize: '12px', marginTop: "3px", marginLeft: "5px" }}>
            {error}
          </div>
        ) : null;
      })()}
    </div>
  )
}

export default Input
